import type * as React from 'react';
import { graphql } from 'react-apollo';

import { useLayer } from '@coursera-web/statsig';
import type { DescriptionPage_ProductVariant as DescriptionPageProductVariant } from '__generated__/graphql-types';
import { branch, compose, withProps } from 'recompose';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import connectToRouter from 'js/lib/connectToRouter';
import user from 'js/lib/user';

import type { ButtonProps } from '@coursera/cds-core';

import withCourseraPlusProductEligibilityCheck from 'bundles/coursera-plus-common/utils/withCourseraPlusProductEligibilityCheck';
import type { EligibilityCheckProps } from 'bundles/coursera-plus-common/utils/withCourseraPlusProductEligibilityCheck';
import type { PropsFromCourseraPlusProductOwnerships } from 'bundles/coursera-plus-common/utils/withCourseraPlusProductOwnerships';
import withCourseraPlusProductOwnerships from 'bundles/coursera-plus-common/utils/withCourseraPlusProductOwnerships';
import {
  getBannerTitle,
  getBannerTitleComponent,
  getEnrollButtonLabel,
  getEnrollButtonTrackingName,
  getFormattedCourseDataFromQuery,
  getIsEnrollButtonDisabled,
  getIsEnrolled,
  getPreEnrollmentFlowVariant,
  getPriceString,
  getShowOpenAidModal,
  getSubtitleElement,
} from 'bundles/enroll-common/components/xdp/enrollSectionUtils';
import withEnrollment from 'bundles/enroll-common/components/xdp/withEnrollment';
import { withPrice } from 'bundles/enroll-common/components/xdp/withNaptimeData';
import { useGetCourseForEnrollButton } from 'bundles/enroll-common/hooks/useGetCourseForEnrollButton';
import GetCourseForXdpEnrollCta from 'bundles/enroll-common/queries/GetCourseForXdpEnrollCtaQuery.graphql';
import GetSpecializationForXdpEnrollCta from 'bundles/enroll-common/queries/GetSpecializationForXdpEnrollCtaQuery.graphql';
import type {
  GetCourseForXdpEnrollCtaQuery,
  GetCourseForXdpEnrollCtaQueryVariables,
} from 'bundles/enroll-common/queries/__generated__/GetCourseForXdpEnrollCtaQuery';
import type {
  GetSpecializationForXdpEnrollCtaQuery,
  GetSpecializationForXdpEnrollCtaQueryVariables,
} from 'bundles/enroll-common/queries/__generated__/GetSpecializationForXdpEnrollCtaQuery';
import {
  canEnrollThroughFreemium,
  isFreemiumModelEnabled,
  previewSurfacePreviewLink,
} from 'bundles/enroll-common/utils/freemiumModelUtils';
import withEnrollModal from 'bundles/enroll-modal/components/withEnrollModal';
import type { Course } from 'bundles/enterprise-legacy-learner-home/types/xdpTypes';
import BannerEnroll from 'bundles/enterprise-legacy-xdp/components/conversion/BannerEnroll';
import {
  withFinaidByUserAndProducts,
  withSpecializations,
} from 'bundles/finaid-common/enhancers/withCoursesAndFinancialAidApplications';
import { withFinancialAidApplicationsByUserAndProduct } from 'bundles/finaid-common/enhancers/withFinancialAidApplications';
import { useNaptime } from 'bundles/naptimejs';
import { CourseTypeMetadataV1 } from 'bundles/naptimejs/resources/courseTypeMetadata.v1';
import withPromotionInfo from 'bundles/promotions/components/withPromotionInfo';
import useCheckS12nUpgradeEligibility from 'bundles/s12n-common/components/s12n-versioning-v2/useCheckS12nUpgradeEligibility';
import type { S12nProductVariant } from 'bundles/s12n-common/constants/s12nProductVariants';
import type ApplicationStoreType from 'bundles/ssr/stores/ApplicationStore';

const WithEnrollModal = withEnrollModal(BannerEnroll);

type PropsFromCaller = {
  onlyShowButton?: boolean;
  isEligibleForPlusMonthlyToAnnualUpgrade: boolean;
  isCourseraPlusEligibleProduct?: boolean;
  showUndoCancellationCTA?: boolean;
  accentColor?: string;
  isSpecialization: boolean;
  courseCount?: number;
  courseId?: string;
  s12nId?: string;
  partnerIds?: string[];
  s12nProductVariant?: S12nProductVariant | DescriptionPageProductVariant;
  s12nCourses?: Course[];
  name?: string;
  slug?: string;
  buttonMinHeightInPixels?: number;
  buttonStyle?: React.CSSProperties;
  enrollBtnCustomStyles?: React.CSSProperties;
  course?: Course;
  onStickyHeader?: boolean;
  wrapperClassName?: React.CSSProperties; // used in BannerEnroll
  buttonRef?: React.RefCallback<HTMLButtonElement>;
  courseTypeMetadataWithVersion?: CourseTypeMetadataV1;
};

type PropsToComponent = PropsFromCourseraPlusProductOwnerships & EligibilityCheckProps;

export const withEnrollButtonData = <InputProps extends PropsFromCaller>(withEnrollmentNotRequired?: boolean) =>
  compose<PropsToComponent, InputProps>(
    connectToStores<Pick<ApplicationStoreType, 'userAgent'>, ApplicationStoreType>(
      ['ApplicationStore'],
      ({ ApplicationStore }) => ({
        userAgent: ApplicationStore.getUserAgent(),
      })
    ),
    connectToRouter((router) => ({ router })),
    // Need to call Course GraphQL entity to check if course is launched already or not as well withEnrollment to render enroll button on SSR
    graphql<PropsFromCaller, GetCourseForXdpEnrollCtaQuery, GetCourseForXdpEnrollCtaQueryVariables, {}>(
      GetCourseForXdpEnrollCta,
      {
        skip: ({ courseId, isSpecialization }) => !(courseId && !isSpecialization),
        options: ({ courseId }) => ({ variables: { id: courseId! } }),
        props: ({ data }) => getFormattedCourseDataFromQuery(data),
      }
    ),
    // Need to call Specialization GraphQL entity to check if s12n is pre enrollment enabled when the learner is logged out
    graphql<PropsFromCaller, GetSpecializationForXdpEnrollCtaQuery, GetSpecializationForXdpEnrollCtaQueryVariables, {}>(
      GetSpecializationForXdpEnrollCta,
      {
        skip: ({ s12nId, isSpecialization }) => !(s12nId && isSpecialization),
        options: ({ s12nId }) => ({ variables: { id: s12nId! } }),
        props: ({ data }) => ({ s12n: data?.Specialization?.queryById }),
      }
    ),
    withEnrollment(withEnrollmentNotRequired),
    branch(
      () => typeof window !== 'undefined' && user.isAuthenticatedUser(),
      // All of these happen on client and should be non-blocking
      compose(
        withPrice(),
        withPromotionInfo(true),
        branch<PropsFromCaller>(
          (props) => Boolean(props.isSpecialization && props.s12nId),
          compose(withSpecializations(true), withFinaidByUserAndProducts(true)),
          withFinancialAidApplicationsByUserAndProduct(undefined, true)
        ),
        branch<PropsFromCaller>(
          ({ isCourseraPlusEligibleProduct }) => !isCourseraPlusEligibleProduct,
          withCourseraPlusProductEligibilityCheck(true)
        ),
        withCourseraPlusProductOwnerships(true)
      )
    ),
    withProps(
      ({
        enrollmentAvailableChoices,
        productPrice,
        s12nDerivative,
        multiS12nDerivatives,
        ownableProducts,
        isSpecialization,
        s12nId,
        s12nCourses,
        courseCount,
        onlyShowButton,
        name,
        isCourseraPlusEligibleProduct,
        financialAidApplicationsByUserAndProduct,
        financialAidApplications,
        productVariant,
        course: _course,
        router,
        isEligibleForPlusMonthlyToAnnualUpgrade,
        showUndoCancellationCTA,
        s12n,
        s12ns,
        courses,
        ...rest
      }: {
        [x: string]: any;
      }) => {
        const isEnrolled = getIsEnrolled({ enrollmentAvailableChoices, isSpecialization });
        const isCapstoneAccessLocked = enrollmentAvailableChoices?.isCapstoneAccessLocked;
        const canEnrollThroughCourseraPlus = enrollmentAvailableChoices?.canEnrollThroughCourseraPlus;
        const preEnrollmentFlowVariant = getPreEnrollmentFlowVariant({
          enrollmentAvailableChoices,
        });

        const course = useGetCourseForEnrollButton({ s12nCourses, course: _course, isSpecialization, isEnrolled });

        const {
          data: { courseTypeMetadataWithVersion },
        } = useNaptime<{
          courseTypeMetadataWithVersion?: CourseTypeMetadataV1;
        }>(() => {
          // We only need to request this data if course is freemium enabled
          if (!course?.id || !isFreemiumModelEnabled(course?.id)) return {};

          return {
            courseTypeMetadataWithVersion: CourseTypeMetadataV1.get(course.id),
          };
        }, [course?.id]);

        const layer = useLayer('monetization-fe-aa-test-layer');
        if (
          canEnrollThroughFreemium({ courseId: course?.id, enrollmentAvailableChoices, courseTypeMetadataWithVersion })
        ) {
          layer.get('monetization-fe-aa-variant', 'control');
        }

        const priceString = getPriceString({
          multiS12nDerivatives,
          s12nDerivative,
          ownableProducts,
          productPrice,
        });
        const TitleComponent = getBannerTitleComponent({
          enrollmentAvailableChoices,
          onlyShowButton,
          preEnrollmentFlowVariant,
        });
        const isStandAloneCourse = !isSpecialization && (!s12ns || s12ns.length < 1);

        const surfacePreviewLinkVariant =
          course && enrollmentAvailableChoices
            ? previewSurfacePreviewLink(enrollmentAvailableChoices, course.id, courseTypeMetadataWithVersion)
            : undefined;

        const title = getBannerTitle({
          priceString,
          enrollmentAvailableChoices,
          isSpecialization,
          onlyShowButton,
          preEnrollmentFlowVariant,
          isStandAloneCourse,
          surfacePreviewLinkVariant,
        });

        const subtitleElem = getSubtitleElement({
          onlyShowButton,
          isCapstoneAccessLocked,
          courseCount,
          isEnrolled,
          canEnrollThroughCourseraPlus,
          courseName: name,
        });

        const buttonSize: ButtonProps['size'] = onlyShowButton ? 'small' : 'medium';
        const trackingName = onlyShowButton ? 'sticky_header' : 'top_banner';

        const { openFinAidInfo, showOpenAidModal } = getShowOpenAidModal({
          financialAidApplications: financialAidApplications?.elements,
          financialAidApplicationsByUserAndProduct,
          isSpecialization,
          s12nId,
          courses: s12nCourses,
        });

        const eligibilityData = useCheckS12nUpgradeEligibility({ s12nId, isSpecialization });
        const isS12nUpgradeEligible = eligibilityData?.upgradeEligibility ?? false;
        const currentS12nId = eligibilityData?.currentEnrolledS12nInFamily?.id ?? '';
        const lastActiveCourseSlug = eligibilityData?.currentEnrolledS12nInFamily?.lastActiveCourse?.slug ?? '';

        // TODO (htran) figure out why the test fails on initial run with this prop missing
        if (!course) {
          return rest;
        }

        return {
          isEnrolled,
          title,
          subtitleElem,
          buttonSize,
          courseId: course.id,
          course,
          buttonLabel: getEnrollButtonLabel({
            enrollmentAvailableChoices,
            isSpecialization,
            course,
            preEnrollmentFlowVariant,
            s12nId,
            s12n,
            s12ns,
            pathname: router.location.pathname,
            isEligibleForPlusMonthlyToAnnualUpgrade,
            showUndoCancellationCTA,
            isS12nUpgradeEligible,
            courseTypeMetadataWithVersion,
          }),
          buttonDisabled: getIsEnrollButtonDisabled({
            enrollmentAvailableChoices,
            isSpecialization,
            course,
          }),
          buttonTrackingName: getEnrollButtonTrackingName({ enrollmentAvailableChoices }),
          isSpecialization,
          // `onlyShowButton` is only true for the sticky SCDP header
          // don't want to check params for that because then we will duplicate opening the enroll modal with the main enroll section
          shouldCheckActionParams: !onlyShowButton,
          showOpenAidModal,
          openFinAidInfo,
          isEligibleForPlusMonthlyToAnnualUpgrade,
          isCourseraPlusEligibleProduct,
          trackingName,
          productVariant,
          s12nProductVariant: productVariant,
          enrollmentAvailableChoices,
          router,
          TitleComponent,
          isS12nUpgradeEligible,
          currentS12nId,
          ...rest,
          partnerIds: isSpecialization ? s12n?.partnerIds : course?.partnerIds,
          lastActiveCourseSlug,
          courseTypeMetadataWithVersion,
        };
      }
    )
  );

// @ts-expect-error TSMIGRATION
export default withEnrollButtonData(true)(WithEnrollModal);
